import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import {
  MarkedTypeEnum,
  QUESTION_NO_WRAPPER_MARGIN_BOTTOM,
  QUESTION_OPTION_PADDING,
} from '@doltech/utils/lib/constants';
import { numberToABC } from '@doltech/utils/lib/text';
import { Tooltip } from 'antd';
import cl from 'classnames';
import { isNil } from 'lodash';
import * as React from 'react';
import styled from 'styled-components';

import { CircleText, QuestionDisplayMode } from '../CircleText/CircleText';
import { colorsV2 } from '../colors-v2';
import { Typography } from '../DesignSystemV2/Typography';
import { QuestionV2 } from '../Question/QuestionV2';

const CircleTextWrapper = styled.div`
  display: flex;
  flex-direction: row;
  border-radius: 8px;
  .question-option-wrapper {
    flex: 1;
  }
  .question-option-result {
    margin-right: 12px;
    align-self: center;
  }
`;

const Main = styled.div`
  .question-circle.shape-circle.vertical-rectangle {
    border-radius: 8px;
  }

  .question-option-wrapper {
    padding: 4px 12px;
  }

  &.${MarkedTypeEnum.CORRECT} {
    .question-circle.question-do-test.shape-circle {
      background-color: ${colorsV2.green100};
    }
  }

  &.${MarkedTypeEnum.MISSING} {
    .question-circle.question-do-test.shape-circle {
      background-color: ${colorsV2.gray20};
      > span {
        color: ${colorsV2.black100};
      }
    }
  }

  &.${MarkedTypeEnum.WRONG} {
    .question-circle.question-do-test.shape-circle {
      background-color: ${colorsV2.red100};
    }
  }

  .circle-text-wrapper.highlight {
    &.${QuestionDisplayMode.QUESTION_OPTION_CORRECT} {
      background: ${colorsV2.green10};
    }
    &.${QuestionDisplayMode.QUESTION_OPTION_CORRECT_MISSING} {
      background: ${colorsV2.black5};
    }
    &.${QuestionDisplayMode.QUESTION_OPTION_WRONG} {
      background: ${colorsV2.primary10};
    }
  }

  .question-no-wrapper {
    margin-bottom: ${QUESTION_NO_WRAPPER_MARGIN_BOTTOM}px;
  }

  .question-option-list {
    display: grid;
    gap: 12px;
    margin-left: ${QUESTION_OPTION_PADDING}px;
    ${fromScreen(1280)} {
      gap: 4px;
    }
  }
`;

export interface MultipleChoiceQuestionResultProps {
  no: number;
  question: string;
  options: OptionModel[];
  userAnswerKeys?: number[];
  markedStatuses: MarkedTypeEnum[];
  correctKeys?: number[];
  maxSelectedOptions: number;
  image?: any;
  className?: string;
}

export interface OptionModel {
  key: number;
  value: string;
}

const getQuestionMarkedStatus = (markedStatuses: MarkedTypeEnum[]) => {
  if (markedStatuses.some((status) => status === MarkedTypeEnum.WRONG)) return MarkedTypeEnum.WRONG;
  if (markedStatuses.some((status) => status === MarkedTypeEnum.MISSING))
    return MarkedTypeEnum.MISSING;
  return MarkedTypeEnum.CORRECT;
};

interface GetTooltipTitleParams {
  markedStatus: MarkedTypeEnum;
}

export const getTooltipTitle = ({ markedStatus }: GetTooltipTitleParams) => {
  let tooltipTitle = '';

  switch (markedStatus) {
    case MarkedTypeEnum.CORRECT: {
      tooltipTitle = 'Correct';
      break;
    }
    case MarkedTypeEnum.WRONG: {
      tooltipTitle = 'Wrong';
      break;
    }
    case MarkedTypeEnum.MISSING: {
      tooltipTitle = 'Not Answer';
      break;
    }
  }

  return tooltipTitle;
};

const MultipleChoiceQuestionResult = ({
  no,
  question,
  options,
  userAnswerKeys,
  markedStatuses,
  correctKeys,
  maxSelectedOptions,
  image,
  className,
  ...rest
}: MultipleChoiceQuestionResultProps) => {
  const lastQuestionSuffix = (maxSelectedOptions > 1 && `-${no + (maxSelectedOptions - 1)}`) || '';

  const questionMarkedStatus = getQuestionMarkedStatus(markedStatuses);

  return (
    <Main
      className={cl('multiple-choice-question-result', questionMarkedStatus, className)}
      {...rest}
    >
      <QuestionV2
        no={`${no}${lastQuestionSuffix}`}
        content={question}
        className="question-no-wrapper"
      />
      {!isNil(image) && (
        <ImageContainer>
          <Img loading="lazy" src={image.url} />
        </ImageContainer>
      )}

      <div className="question-option-list">
        {options.map((option) => {
          const { key, value } = option;
          const correctKeyIndex = correctKeys.findIndex((correctKey) => key === correctKey);
          const isCorrectAnswer = correctKeyIndex > -1;

          let displayMode: QuestionDisplayMode = QuestionDisplayMode.QUESTION_OPTION_BASIC;
          let currentMarkedStatus = '';

          const markedStatus = markedStatuses.find((_, index) => index === correctKeyIndex);
          if (isCorrectAnswer) {
            if (markedStatus === MarkedTypeEnum.MISSING) {
              displayMode = QuestionDisplayMode.QUESTION_OPTION_CORRECT_MISSING;
              currentMarkedStatus = MarkedTypeEnum.MISSING;
            } else {
              displayMode = QuestionDisplayMode.QUESTION_OPTION_CORRECT;
              currentMarkedStatus = MarkedTypeEnum.CORRECT;
            }
          } else if (isNil(userAnswerKeys) === false && userAnswerKeys.includes(key)) {
            displayMode = QuestionDisplayMode.QUESTION_OPTION_WRONG;
            currentMarkedStatus = MarkedTypeEnum.WRONG;
          }

          let isHighlight = markedStatuses.some((status) => status === currentMarkedStatus);

          if (markedStatus === MarkedTypeEnum.WRONG && isCorrectAnswer) {
            isHighlight = false;
          }

          const tooltipTitle = getTooltipTitle({
            markedStatus: currentMarkedStatus as MarkedTypeEnum,
          });

          return (
            <Tooltip key={key} title={isHighlight ? tooltipTitle : ''}>
              <CircleTextWrapper
                className={cl('circle-text-wrapper', displayMode, { highlight: isHighlight })}
              >
                <CircleText
                  className={cl('question-option-wrapper')}
                  circleText={numberToABC(key + 1)}
                  displayMode={displayMode}
                  text={`${value}`}
                />
                {currentMarkedStatus === MarkedTypeEnum.WRONG && (
                  <div className="question-option-result">
                    <Typography.Text variant="medium/14" color="primary100">
                      Sai
                    </Typography.Text>
                  </div>
                )}
              </CircleTextWrapper>
            </Tooltip>
          );
        })}
      </div>
    </Main>
  );
};

const ImageContainer = styled.section`
  margin-bottom: 4px;
  text-align: center;
`;

const Img = styled.img`
  max-width: 100%;
  height: auto;
`;

export default React.memo(MultipleChoiceQuestionResult);
